import { useEventContext } from "Bands/contexts";
import { BAND_PATHS } from "Bands/router";
import { dataMetaSelector } from "Bands/state";
import { dispatchCopyEventBlocks } from "Bands/state/data/actions/eventBlocks/copyEventBlock";
import { dispatchDeleteEventBlock } from "Bands/state/data/actions/eventBlocks/deleteEventBlock";
import {
  BAND_ACTIONS,
  EventBlocks,
  EventBlocksType,
  EventUniqueBlocks,
  IEvent,
  IIconBandSetting,
} from "Bands/types";
import { getEventBlockType, useBandRoute } from "Bands/utils";
import { useClearActionMetaOnUnMount } from "Venues/utils";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { copy } from "shared/assets";
import { FormBuilder, Message } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { API_ENDPOINTS } from "shared/constants";
import { Spacer } from "shared/layout";
import {
  IAPIState,
  IFieldType,
  IFormFields,
  IFormOption,
  IStatus,
} from "shared/types";
import {
  DateFormat,
  formatDateTime,
  getLastIdFromPath,
  isVowel,
} from "shared/utils";

interface Props {
  sourceEvent?: IEvent | null;
  targetEvent?: IEvent | null;
  selectedEventBlocks: IFormOption[];
  blockType: EventBlocksType;
  handleModalClose: () => void;
  deleteBlockId?: string;
  infoBlockType?: IIconBandSetting;
}

export const CopyBlockForm: FC<Props> = ({
  sourceEvent,
  targetEvent,
  selectedEventBlocks,
  blockType,
  handleModalClose,
  deleteBlockId,
  infoBlockType,
}) => {
  const dispatch = useDispatch();

  const { pushRoute } = useBandRoute();
  const { refreshAllEventBlocks } = useEventContext();

  const { api, id: createdCopyId } = useSelector(
    dataMetaSelector(BAND_ACTIONS.copyEventBlocks)
  );

  const { api: deleteApi } = useSelector(
    dataMetaSelector(BAND_ACTIONS.deleteEventBlock)
  );

  const [selectedBlockId, setSelectedBlockId] = useState<string>(
    selectedEventBlocks.length === 1 ? selectedEventBlocks[0].value : ""
  );

  const selectedBlock = useMemo(
    () => selectedEventBlocks.find(({ value }) => value === selectedBlockId),
    [selectedBlockId, selectedEventBlocks]
  );

  const [includeRsvpUsers, setIncludeRsvpUsers] = useState<boolean>();

  const renderCopy = useCallback(
    (c: string) => {
      return c
        .replaceAll("%blockTitle", String(selectedBlock?.label))
        .replaceAll(
          "%blockWarning",
          copy.copyBlock.blockWarnings[
            blockType as keyof typeof copy.copyBlock.blockWarnings
          ] || ""
        )
        .replaceAll(
          "%sourceEventDate",
          formatDateTime(sourceEvent?.date!, {
            dateFormat: DateFormat.dmy,
            dateOnly: true,
          }) || ""
        )
        .replaceAll("%sourceEventTitle", sourceEvent?.title || "")
        .replaceAll("%targetEventTitle", targetEvent?.title || "")
        .replaceAll(
          "%targetEventDate",
          formatDateTime(targetEvent?.date!, {
            dateFormat: DateFormat.dmy,
            dateOnly: true,
          }) || ""
        );
    },
    [
      blockType,
      selectedBlock?.label,
      sourceEvent?.date,
      sourceEvent?.title,
      targetEvent?.date,
      targetEvent?.title,
    ]
  );

  const fields: IFormFields = [
    {
      type: IFieldType.radioGroup,
      name: "selectedBlock",
      label: `Please select a${
        isVowel(
          (infoBlockType
            ? infoBlockType.label
            : copy.copyBlock.blockTypeCopies[
                blockType as keyof typeof copy.copyBlock.blockTypeCopies
              ])[0]
        )
          ? "n"
          : ""
      } ${
        infoBlockType
          ? `${infoBlockType.label} block`
          : copy.copyBlock.blockTypeCopies[
              blockType as keyof typeof copy.copyBlock.blockTypeCopies
            ]
      } configuration from "${sourceEvent?.title} (${formatDateTime(
        sourceEvent!.date,
        {
          dateOnly: true,
        }
      )})" to import`,
      options: selectedEventBlocks,
      isInline: false,
      style: {
        gridColumn: "span 2",
        mobile: {
          gridColumn: "span 1",
        },
      },
    },
  ];

  useClearActionMetaOnUnMount(BAND_ACTIONS.copyEventBlocks);
  useClearActionMetaOnUnMount(BAND_ACTIONS.deleteEventBlock);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const redirectAfterSubmit = () => {
    if (!createdCopyId) return;

    const paths = {
      [EventBlocks.schedule]: BAND_PATHS.EDIT_SCHEDULE_BLOCK,
      [EventBlocks.accommodation]: BAND_PATHS.EDIT_ACCOMMODATION_BLOCK,
      [EventBlocks.travel]: BAND_PATHS.EDIT_TRAVEL_BLOCK,
      [EventUniqueBlocks.people]: BAND_PATHS.EDIT_PEOPLE_BLOCK,
      [EventBlocks.guestlist]: BAND_PATHS.EDIT_GUESTLIST_BLOCK,
      [EventBlocks.infoBlock]: BAND_PATHS.EDIT_INFO_BLOCK,
    };

    refreshAllEventBlocks();

    pushRoute(paths[blockType as keyof typeof paths], {
      params: {
        eventId: targetEvent?.id!,
        blockId: getLastIdFromPath(createdCopyId),
      },
    });

    dispatch(
      addTemporaryToast({
        message: copy.copyBlock.success,
        status: IStatus.success,
      })
    );

    handleModalClose();
  };

  const onFinalSubmit = useCallback(
    ({ includeRsvpUsers: includeRsvpUsersValue }) => {
      blockType === EventUniqueBlocks.people &&
        setIncludeRsvpUsers(includeRsvpUsersValue === "true");

      if (deleteBlockId) {
        dispatchDeleteEventBlock(dispatch, deleteBlockId, blockType);
      } else {
        dispatchCopyEventBlocks(
          dispatch,
          [API_ENDPOINTS.EVENT(targetEvent?.id!)],
          [getEventBlockType(blockType)?.api.item(selectedBlockId)!],
          includeRsvpUsers
        );
      }
    },
    [
      blockType,
      deleteBlockId,
      dispatch,
      selectedBlockId,
      targetEvent?.id,
      includeRsvpUsers,
    ]
  );

  useEffect(() => {
    if (api?.status === IAPIState.succeeded && createdCopyId) {
      redirectAfterSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    api?.status,
    blockType,
    deleteBlockId,
    dispatch,
    pushRoute,
    targetEvent?.id,
  ]);

  useEffect(() => {
    if (deleteApi?.status === IAPIState.succeeded) {
      dispatchCopyEventBlocks(
        dispatch,
        [API_ENDPOINTS.EVENT(targetEvent?.id!)],
        [getEventBlockType(blockType)?.api.item(selectedBlockId)!],
        includeRsvpUsers
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteApi?.status, dispatch, selectedBlockId, targetEvent?.id]);

  const formFields: IFormFields = useMemo(() => {
    if (
      blockType !== EventUniqueBlocks.people ||
      !(selectedEventBlocks?.[0] as any)?.hasUsers
    )
      return [];

    return [
      {
        type: IFieldType.radioGroup,
        name: "includeRsvpUsers",
        label: "",
        options: [
          {
            label: copy.copyBlock.rsvp.copyRSVP,
            value: "true",
            defaultChecked: true,
          },
          { label: copy.copyBlock.rsvp.noCopyRSVP, value: "false" },
        ],
        isInline: false,
        style: {
          gridColumn: "span 2",
          mobile: {
            gridColumn: "span 1",
          },
        },
      },
    ];
  }, [blockType, selectedEventBlocks]);

  const warningComponent = useMemo(() => {
    if (!selectedBlockId) return;

    const showRSVPCopy =
      blockType === EventUniqueBlocks.people &&
      (selectedBlock as any)?.hasUsers;

    return (
      <Spacer size="m">
        <Message status={IStatus.info}>
          {`${renderCopy(copy.copyBlock.base)}${
            showRSVPCopy ? copy.copyBlock.eventPeopleBlockRSVP : "."
          }`}
          <br /> <br />
          {renderCopy(copy.copyBlock[blockType])}
        </Message>
        <FormBuilder
          onSubmit={onFinalSubmit}
          fields={formFields}
          buttons={{
            primary: {
              children: "Confirm",
            },
            secondary: {
              children: "Cancel",
              onClick: handleModalClose,
            },
          }}
          apiError={api?.error || deleteApi?.error}
          apiErrorMessage={
            api?.error?.data?.violations?.[0]?.message ||
            deleteApi?.error?.data?.violations?.[0]?.message
          }
          isSubmitting={api?.status === IAPIState.pending}
        ></FormBuilder>
      </Spacer>
    );
  }, [
    api?.error,
    api?.status,
    blockType,
    deleteApi?.error,
    formFields,
    handleModalClose,
    onFinalSubmit,
    renderCopy,
    selectedBlock,
    selectedBlockId,
  ]);

  return (
    <>
      {warningComponent ? (
        warningComponent
      ) : (
        <FormBuilder
          isSubmitting={api?.status === IAPIState.pending}
          fields={fields}
          onSubmit={(form: { selectedBlock: string }) => {
            setSelectedBlockId(form.selectedBlock);
          }}
          buttons={{
            primary: {
              children: "Continue",
            },
            secondary: {
              children: "Cancel",
              onClick: handleModalClose,
            },
          }}
          resetFormOnSubmit={true}
          apiError={api?.error}
        />
      )}
    </>
  );
};
